/*
INSIGHTFUL CARE SOLUTIONS — WIREFRAME PACK FOR CURSOR AI
Low‑fidelity, production‑ready scaffold using React + Tailwind classes.
All copy is placeholder; replace with final content. Sections are labeled clearly.

INCLUDED:
1) Route-free single page wireframe (anchors) — easily split into routes later
2) Component library: Section, Container, Card, Pill, FAQItem, Input, Select, TextArea
3) Content Matrix (JSON) at bottom for Cursor to consume and inject
4) Accessibility: landmarks, aria‑labels, keyboard‑focusable nav
5) Responsive: mobile‑first, sensible breakpoints, stack → grid

HOW TO USE IN CURSOR
- Create a Vite React + Tailwind project.
- Paste this file into src/Wireframe.jsx and import in main.jsx.
- Replace placeholder text/images with real content or bind to CMS.
*/

import { useState, useEffect } from 'react';

// Import all images
import logoImg from './images/logo.png';
import landImg from './images/land.jpg';
import mentalhealthImg from './images/mentalhealth.jpg';
import bikingImg from './images/biking.jpg';
import groupImg from './images/group.jpg';
import babyImg from './images/baby.jpg';
import initialImg from './images/initial.png';
import followupImg from './images/follow up.png';
import keyshannaProfileImg from './images/keyshanna pic-modified (1).jpg';
import lindaProfileImg from './images/linda pic-modified (1).jpg';
import keyshannaTeamCircleImg from './images/keyshanna pic-modified.png';
import lindaTeamCircleImg from './images/linda pic-modified.png';
import hs1Img from './images/hs1.jpg';
import hs2Img from './images/hs2.jpg';
import hs3Img from './images/hs3.jpg';

// Import logo SVGs
import circlesLogo from './given-images/logos/logos-to-test/circles.svg';
import treeLogo from './given-images/logos/logos-to-test/tree.svg';
import handTreeLogo from './given-images/logos/logos-to-test/hand tree.svg';
import treeNoBgLogo from './given-images/logos/logos-to-test/tree no bg.svg';
import treeNoBgBrownLogo from './given-images/logos/logos-to-test/tree no bg-brown.svg';
import treeNoBgLightBrownLogo from './given-images/logos/logos-to-test/tree no bg-lightbrown.svg';
import insightfulCareSolutionsLogo from './given-images/logos/logos-to-test/Insightful care Solutions (1).svg';
import insightLogo from './given-images/logos/logos-to-test/insightlogo-removebg-preview.png';
import sadImage from './given-images/sad-7078278.svg';
import girlImage from './given-images/girl-6007648.svg';

// ----------------------------
// BASICS
// ----------------------------

function Container({ children, className = "" }) {
  return (
    <div className={`mx-auto w-full max-w-7xl px-4 sm:px-5 md:px-6 lg:px-8 xl:px-10 ${className}`}>{children}</div>
  );
}

function Section({ id, title, subtitle, children, tone = "light", className = "" }) {
  const tones = {
    light: "bg-neutral-50",
    white: "bg-white",
    band: "bg-neutral-100",
  };
  return (
    <section id={id} aria-label={title} className={`${tones[tone]} py-10 sm:py-12 md:py-14 lg:py-16 xl:py-20 ${className}`}>
      <Container>
        {title && (
          <div className="mb-6 sm:mb-8 max-w-3xl">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-neutral-900 break-words">{title}</h2>
            {subtitle && <p className="mt-2 sm:mt-3 text-sm sm:text-base md:text-lg text-neutral-600 break-words">{subtitle}</p>}
          </div>
        )}
        {children}
      </Container>
    </section>
  );
}

function Card({ children, className = "" }) {
  return (
    <div className={`rounded-xl sm:rounded-2xl border border-neutral-300 bg-white p-4 sm:p-5 md:p-6 ${className}`}>{children}</div>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-neutral-300 px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm text-neutral-700 break-words">
      {children}
    </span>
  );
}

function FAQItem({ q, a }) {
  return (
    <details className="group border-b border-neutral-200 py-4">
      <summary className="flex cursor-pointer items-center justify-between text-sm sm:text-base font-medium min-h-[44px]">
        <span className="pr-4 break-words flex-1 text-left">{q}</span>
        <span className="ml-4 inline-flex h-8 w-8 items-center justify-center rounded-full border border-neutral-300 text-neutral-600 group-open:rotate-45 transition flex-shrink-0">+</span>
      </summary>
      <div className="mt-3 text-neutral-600 text-sm sm:text-base leading-relaxed break-words space-y-3 [&_a]:underline [&_a]:underline-offset-2 [&_a]:font-medium [&_a]:text-neutral-800 [&_a]:hover:no-underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5">{a}</div>
    </details>
  );
}

function Input({ label, ...props }) {
  return (
    <label className="block text-sm sm:text-base">
      <span className="font-medium text-neutral-800">{label}</span>
      <input className="mt-1 w-full rounded-xl border border-neutral-300 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base outline-none focus:ring-2 focus:ring-neutral-800 min-h-[44px]" {...props} />
    </label>
  );
}

function Select({ label, children, ...props }) {
  return (
    <label className="block text-sm sm:text-base">
      <span className="font-medium text-neutral-800">{label}</span>
      <select className="mt-1 w-full rounded-xl border border-neutral-300 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base outline-none focus:ring-2 focus:ring-neutral-800 min-h-[44px]" {...props}>
        {children}
      </select>
    </label>
  );
}

function TextArea({ label, rows = 5, ...props }) {
  return (
    <label className="block text-sm sm:text-base">
      <span className="font-medium text-neutral-800">{label}</span>
      <textarea rows={rows} className="mt-1 w-full rounded-xl border border-neutral-300 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base outline-none focus:ring-2 focus:ring-neutral-800 resize-y" {...props} />
    </label>
  );
}

function FounderAvailability({ compact = false }) {
  return (
    <div
      className={compact
        ? 'rounded-xl border-l-4 border-neutral-900 bg-neutral-50 px-3 py-3'
        : 'rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-5 sm:px-6 sm:py-6'}
      aria-label="Certified Psychiatric Mental Health Nurse Practitioner availability"
    >
      {!compact && (
        <h3 className="text-base sm:text-lg font-semibold text-neutral-900">
          When you can book
        </h3>
      )}
      <p className={`${compact ? 'text-sm' : 'mt-2 text-sm sm:text-base'} text-neutral-700 leading-relaxed`}>
        {compact
          ? 'Available Mon–Fri, 9 AM–9 PM, in 1-hour sessions.'
          : 'Each Certified Psychiatric Mental Health Nurse Practitioner is available Monday through Friday from 9:00 AM to 9:00 PM. Appointments are offered in one-hour blocks, so you can choose a time that fits your day — morning, afternoon, or evening.'}
      </p>
      {!compact && (
        <dl className="mt-4 grid sm:grid-cols-3 gap-3">
          <div className="rounded-xl bg-white border border-neutral-200 px-3 py-3">
            <dt className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Days</dt>
            <dd className="mt-1 text-sm font-medium text-neutral-900">Monday – Friday</dd>
          </div>
          <div className="rounded-xl bg-white border border-neutral-200 px-3 py-3">
            <dt className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Hours</dt>
            <dd className="mt-1 text-sm font-medium text-neutral-900">9:00 AM – 9:00 PM</dd>
          </div>
          <div className="rounded-xl bg-white border border-neutral-200 px-3 py-3">
            <dt className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Sessions</dt>
            <dd className="mt-1 text-sm font-medium text-neutral-900">1-hour blocks</dd>
          </div>
        </dl>
      )}
    </div>
  );
}

function NavLink({ onClick, children, isActive = false }) {
  return (
    <button 
      onClick={onClick} 
      className={`text-sm hover:text-neutral-800 focus:outline-none transition-colors duration-200 ${
        isActive ? 'text-neutral-900 font-medium' : 'text-neutral-700'
      }`}
    >
      {children}
    </button>
  );
}

function InstagramIcon({ className = "h-5 w-5" }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <rect x="3.25" y="3.25" width="17.5" height="17.5" rx="5.25" />
      <circle cx="12" cy="12" r="4.25" />
      <circle cx="17.3" cy="6.7" r="1.05" fill="currentColor" stroke="none" />
    </svg>
  );
}

function InstagramLink({ href, compact = false, className = "" }) {
  if (compact) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Visit our Instagram"
        className={`inline-flex items-center justify-center rounded-lg border border-neutral-300 px-2.5 py-2 text-neutral-700 transition-colors hover:bg-neutral-100 hover:text-neutral-900 min-h-[44px] min-w-[44px] ${className}`}
      >
        <InstagramIcon className="h-5 w-5" />
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Visit our Instagram"
      className={`inline-flex items-center gap-2 rounded-xl border border-neutral-300 px-3.5 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100 hover:text-neutral-900 min-h-[44px] ${className}`}
    >
      <InstagramIcon className="h-5 w-5" />
      <span>Instagram</span>
    </a>
  );
}

const BUSINESS_PHONE_E164 = "+16093342782";
const BUSINESS_PHONE_DISPLAY = "(609) 334-2782";

function PhoneIcon({ className = "h-5 w-5" }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

/** tel: link + display; use className for layout (header chip, footer, etc.) */
function BusinessPhoneLink({ className = "", iconClassName = "h-5 w-5", iconOnly = false }) {
  return (
    <a
      href={`tel:${BUSINESS_PHONE_E164}`}
      className={`inline-flex items-center justify-center text-neutral-700 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800 focus-visible:ring-offset-2 rounded-lg ${iconOnly ? "gap-0" : "gap-2"} ${className}`}
      aria-label={`Call Insightful Care Solutions at ${BUSINESS_PHONE_DISPLAY}`}
    >
      <PhoneIcon className={`${iconClassName} flex-shrink-0`} />
      {!iconOnly && <span className="tabular-nums tracking-tight font-medium">{BUSINESS_PHONE_DISPLAY}</span>}
    </a>
  );
}

function AnchorTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

// Click handler for navigation
function AnchorToWithState(id, setActiveSection) {
  setActiveSection(id);
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

// ----------------------------
// PAGE SECTIONS (WIREFRAME)
// ----------------------------

export default function App() {
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Active navigation state
  const [activeSection, setActiveSection] = useState('home');
  
  // Font switcher state
  const [currentFont, setCurrentFont] = useState('Merriweather');
  const [isFontSwitcherOpen, setIsFontSwitcherOpen] = useState(false);
  
  // Color switcher state
  const [currentPalette, setCurrentPalette] = useState('medium-skin');
  const [isColorSwitcherOpen, setIsColorSwitcherOpen] = useState(false);
  
  // Logo switcher state
  const [currentLogo, setCurrentLogo] = useState('insight-logo');
  
  // Fixed logo configuration (saved from slider settings)
  const headerLogoSize = 250;
  const headerHeight = 150;
  
  // Responsive logo size based on screen width
  const getResponsiveLogoSize = () => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width < 640) return Math.min(120, headerHeight - 20); // Mobile
      if (width < 768) return Math.min(150, headerHeight - 20); // Small tablet
      return Math.min(headerLogoSize, headerHeight - 10); // Desktop
    }
    return Math.min(headerLogoSize, headerHeight - 10);
  };
  
  const [logoSize, setLogoSize] = useState(getResponsiveLogoSize());
  
  useEffect(() => {
    const handleResize = () => {
      setLogoSize(getResponsiveLogoSize());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Disorder dropdown state
  const [openDisorder, setOpenDisorder] = useState(null);
  const [showCountyHotlines, setShowCountyHotlines] = useState(false);
  
  // Available color palettes
  const colorPalettes = {
    'classic-neutral': {
      name: 'Classic Neutral',
      description: 'Pure black, white, and greys',
      colors: {
        'bg-50': '#fafafa',
        'bg-100': '#f5f5f5',
        'bg-200': '#e5e5e5',
        'bg-300': '#d4d4d4',
        'bg-400': '#a3a3a3',
        'bg-500': '#737373',
        'bg-600': '#525252',
        'bg-700': '#404040',
        'bg-800': '#262626',
        'bg-900': '#171717',
        'text-50': '#fafafa',
        'text-100': '#f5f5f5',
        'text-200': '#e5e5e5',
        'text-300': '#d4d4d4',
        'text-400': '#a3a3a3',
        'text-500': '#737373',
        'text-600': '#525252',
        'text-700': '#404040',
        'text-800': '#262626',
        'text-900': '#171717',
        'border': '#e5e5e5',
        'button': '#171717',
        'button-hover': '#404040',
      }
    },
    'warm-neutral': {
      name: 'Warm Neutral',
      description: 'Warm greys with beige undertones',
      colors: {
        'bg-50': '#faf9f7',
        'bg-100': '#f5f3f0',
        'bg-200': '#e8e5e0',
        'bg-300': '#d4d0c8',
        'bg-400': '#a39e94',
        'bg-500': '#807a70',
        'bg-600': '#5f5a52',
        'bg-700': '#4a4640',
        'bg-800': '#35322e',
        'bg-900': '#2a2724',
        'text-50': '#faf9f7',
        'text-100': '#f5f3f0',
        'text-200': '#e8e5e0',
        'text-300': '#d4d0c8',
        'text-400': '#a39e94',
        'text-500': '#807a70',
        'text-600': '#5f5a52',
        'text-700': '#4a4640',
        'text-800': '#35322e',
        'text-900': '#2a2724',
        'border': '#e8e5e0',
        'button': '#2a2724',
        'button-hover': '#4a4640',
      }
    },
    'cool-neutral': {
      name: 'Cool Neutral',
      description: 'Cool greys with blue undertones',
      colors: {
        'bg-50': '#f8f9fa',
        'bg-100': '#f1f3f5',
        'bg-200': '#e2e6ea',
        'bg-300': '#ced4da',
        'bg-400': '#9ca3af',
        'bg-500': '#6b7280',
        'bg-600': '#4b5563',
        'bg-700': '#374151',
        'bg-800': '#1f2937',
        'bg-900': '#111827',
        'text-50': '#f8f9fa',
        'text-100': '#f1f3f5',
        'text-200': '#e2e6ea',
        'text-300': '#ced4da',
        'text-400': '#9ca3af',
        'text-500': '#6b7280',
        'text-600': '#4b5563',
        'text-700': '#374151',
        'text-800': '#1f2937',
        'text-900': '#111827',
        'border': '#e2e6ea',
        'button': '#111827',
        'button-hover': '#374151',
      }
    },
    'light-skin': {
      name: 'Light Skin Tones',
      description: 'Warm light beige and tan',
      colors: {
        'bg-50': '#fdf8f4',
        'bg-100': '#f9f0e8',
        'bg-200': '#f0e0d0',
        'bg-300': '#e4cbb0',
        'bg-400': '#d4a574',
        'bg-500': '#c68642',
        'bg-600': '#9d6b35',
        'bg-700': '#7a5329',
        'bg-800': '#5c3e1f',
        'bg-900': '#3d2915',
        'text-50': '#fdf8f4',
        'text-100': '#f9f0e8',
        'text-200': '#f0e0d0',
        'text-300': '#e4cbb0',
        'text-400': '#d4a574',
        'text-500': '#c68642',
        'text-600': '#9d6b35',
        'text-700': '#7a5329',
        'text-800': '#5c3e1f',
        'text-900': '#3d2915',
        'border': '#f0e0d0',
        'button': '#5c3e1f',
        'button-hover': '#7a5329',
      }
    },
    'medium-skin': {
      name: 'Medium Skin Tones',
      description: 'Rich caramel and brown tones',
      colors: {
        'bg-50': '#f9f5f0',
        'bg-100': '#f0e6d8',
        'bg-200': '#e0ccb3',
        'bg-300': '#c9a67a',
        'bg-400': '#b87d50',
        'bg-500': '#a0663d',
        'bg-600': '#8b5a3c',
        'bg-700': '#6b4423',
        'bg-800': '#4a2c1a',
        'bg-900': '#2d1a0f',
        'text-50': '#f9f5f0',
        'text-100': '#f0e6d8',
        'text-200': '#e0ccb3',
        'text-300': '#c9a67a',
        'text-400': '#b87d50',
        'text-500': '#a0663d',
        'text-600': '#8b5a3c',
        'text-700': '#6b4423',
        'text-800': '#4a2c1a',
        'text-900': '#2d1a0f',
        'border': '#e0ccb3',
        'button': '#4a2c1a',
        'button-hover': '#6b4423',
      }
    },
    'deep-neutral': {
      name: 'Deep Neutral',
      description: 'Dark mode with light text',
      colors: {
        'bg-50': '#171717',
        'bg-100': '#1f1f1f',
        'bg-200': '#2a2a2a',
        'bg-300': '#404040',
        'bg-400': '#525252',
        'bg-500': '#737373',
        'bg-600': '#a3a3a3',
        'bg-700': '#d4d4d4',
        'bg-800': '#e5e5e5',
        'bg-900': '#f5f5f5',
        'text-50': '#171717',
        'text-100': '#1f1f1f',
        'text-200': '#2a2a2a',
        'text-300': '#404040',
        'text-400': '#525252',
        'text-500': '#737373',
        'text-600': '#a3a3a3',
        'text-700': '#d4d4d4',
        'text-800': '#e5e5e5',
        'text-900': '#f5f5f5',
        'border': '#2a2a2a',
        'button': '#f5f5f5',
        'button-hover': '#d4d4d4',
      }
    }
  };
  
  // Available fonts for testing
  const availableFonts = [
    { name: 'Inter', label: 'Inter', category: 'Sans-serif' },
    { name: 'Roboto', label: 'Roboto', category: 'Sans-serif' },
    { name: 'Open Sans', label: 'Open Sans', category: 'Sans-serif' },
    { name: 'Montserrat', label: 'Montserrat', category: 'Sans-serif' },
    { name: 'Poppins', label: 'Poppins', category: 'Sans-serif' },
    { name: 'Lato', label: 'Lato', category: 'Sans-serif' },
    { name: 'Source Sans Pro', label: 'Source Sans Pro', category: 'Sans-serif' },
    { name: 'Work Sans', label: 'Work Sans', category: 'Sans-serif' },
    { name: 'Raleway', label: 'Raleway', category: 'Sans-serif' },
    { name: 'Playfair Display', label: 'Playfair Display', category: 'Serif' },
    { name: 'Merriweather', label: 'Merriweather', category: 'Serif' },
    { name: 'Crimson Pro', label: 'Crimson Pro', category: 'Serif' },
  ];
  
  // Available logos
  const availableLogos = [
    { id: 'insight-logo', name: 'Insight Logo', src: insightLogo },
    { id: 'default', name: 'Default', src: logoImg },
    { id: 'circles', name: 'Circles', src: circlesLogo },
    { id: 'tree', name: 'Tree', src: treeLogo },
    { id: 'hand-tree', name: 'Hand Tree', src: handTreeLogo },
    { id: 'tree-no-bg', name: 'Tree (No BG)', src: treeNoBgLogo },
    { id: 'tree-brown', name: 'Tree Brown', src: treeNoBgBrownLogo },
    { id: 'tree-lightbrown', name: 'Tree Light Brown', src: treeNoBgLightBrownLogo },
    { id: 'insightful-care-solutions', name: 'Insightful Care Solutions', src: insightfulCareSolutionsLogo },
  ];
  
  // Get current logo source
  const currentLogoSource = availableLogos.find(logo => logo.id === currentLogo)?.src || logoImg;
  
  // Apply font to document
  useEffect(() => {
    const root = document.documentElement;
    root.style.fontFamily = `'${currentFont}', system-ui, ${currentFont.includes('Display') || currentFont.includes('Merriweather') || currentFont.includes('Crimson') ? 'serif' : 'sans-serif'}`;
  }, [currentFont]);
  
  // Apply color palette to document
  useEffect(() => {
    const root = document.body;
    const palette = colorPalettes[currentPalette];
    if (palette) {
      Object.entries(palette.colors).forEach(([key, value]) => {
        root.style.setProperty(`--color-${key}`, value);
      });
      
      // Create or update dynamic style tag for overriding Tailwind classes
      let styleTag = document.getElementById('dynamic-color-override');
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = 'dynamic-color-override';
        document.head.appendChild(styleTag);
      }
      
      // Override Tailwind classes with CSS variables
      styleTag.textContent = `
        .bg-neutral-50 { background-color: var(--color-bg-50) !important; }
        .bg-neutral-100 { background-color: var(--color-bg-100) !important; }
        .bg-neutral-200 { background-color: var(--color-bg-200) !important; }
        .bg-neutral-300 { background-color: var(--color-bg-300) !important; }
        .bg-neutral-400 { background-color: var(--color-bg-400) !important; }
        .bg-neutral-500 { background-color: var(--color-bg-500) !important; }
        .bg-neutral-600 { background-color: var(--color-bg-600) !important; }
        .bg-neutral-700 { background-color: var(--color-bg-700) !important; }
        .bg-neutral-800 { background-color: var(--color-bg-800) !important; }
        .bg-neutral-900 { background-color: var(--color-bg-900) !important; }
        .bg-white { background-color: var(--color-bg-50) !important; }
        
        .text-neutral-50 { color: var(--color-text-50) !important; }
        .text-neutral-100 { color: var(--color-text-100) !important; }
        .text-neutral-200 { color: var(--color-text-200) !important; }
        .text-neutral-300 { color: var(--color-text-300) !important; }
        .text-neutral-400 { color: var(--color-text-400) !important; }
        .text-neutral-500 { color: var(--color-text-500) !important; }
        .text-neutral-600 { color: var(--color-text-600) !important; }
        .text-neutral-700 { color: var(--color-text-700) !important; }
        .text-neutral-800 { color: var(--color-text-800) !important; }
        .text-neutral-900 { color: var(--color-text-900) !important; }
        
        .border-neutral-200 { border-color: var(--color-border) !important; }
        .border-neutral-300 { border-color: var(--color-border) !important; }
        
        button.bg-neutral-900, .bg-neutral-900 { background-color: var(--color-button) !important; }
        button.bg-neutral-900:hover, .bg-neutral-900:hover { background-color: var(--color-button-hover) !important; }
        button.hover\\:bg-neutral-700:hover { background-color: var(--color-button-hover) !important; }
      `;
    }
  }, [currentPalette]);
  
  // Scroll detection for active navigation
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'mission', 'services', 'founders', 'reviews', 'faq', 'resources', 'contact'];
      const scrollPosition = window.scrollY + 100; // Offset for header height
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Form state management
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    inquiryType: 'Service Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(''); // 'success', 'error', or ''

  // Google Apps Script configuration
  // TODO: Replace with your deployed GAS Web App URL after deployment
  const GAS_WEB_APP_URL = 'https://script.google.com/macros/s/AKfycbz80VgqKz8SuGgnkGc1WMclyWKW7yXGYMVeezfnq2jJ2PrFX8M-U1uRaef1k2g5C4E/exec';
  const BOOKING_URL = 'https://ehr.charmtracker.com/publicCal.sas?method=getCal&digest=db1625042001420a095d05e1306157be2a9a87e404568d31a36f760f572ddf9b3da030e7f293979a6c5748646da4f775d2bec0af04f23226';
  const INSTAGRAM_URL = 'https://www.instagram.com/insightfulcaresolutions?igsh=Z2x5YXdvbmt6bGxk&utm_source=qrs';

  // ChARM allowlists the referring origin; noreferrer would strip Referer and breaks their embed check.
  const openExternalBooking = () => window.open(BOOKING_URL, '_blank', 'noopener');

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Prepare data for Google Apps Script
      const formPayload = {
        from_name: formData.fullName,
        from_email: formData.email,
        phone: formData.phone,
        inquiry_type: formData.inquiryType,
        message: formData.message
      };

      // Send to Google Apps Script
      const response = await fetch(GAS_WEB_APP_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formPayload)
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          inquiryType: 'Service Inquiry',
          message: ''
        });
      } else {
        throw new Error(result.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Email sending failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Form input handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const conditions = [
    "Anxiety & Panic",
    "Depression",
    "Bipolar & Mood Disorders",
    "OCD",
    "PTSD",
    "Adjustment Disorder",
    "ADHD",
  ];

  const disorders = [
    {
      id: 'anxiety',
      name: 'Anxiety & Panic Disorders',
      description: 'Anxiety disorders involve excessive fear, worry, and nervousness that interfere with daily life. We provide comprehensive evaluation and treatment for various anxiety conditions.',
      bullets: [
        'Generalized Anxiety Disorder (GAD)',
        'Panic Disorder',
        'Social Anxiety Disorder',
        'Specific Phobias'
      ],
      image1: girlImage,
      image2: bikingImg
    },
    {
      id: 'depression',
      name: 'Depression',
      description: 'Depression is a mood disorder that causes persistent feelings of sadness, loss of interest, and affects how you think, feel, and handle daily activities.',
      bullets: [
        'Major Depressive Disorder',
        'Persistent Depressive Disorder',
        'Seasonal Affective Disorder',
        'Treatment-Resistant Depression'
      ],
      image1: sadImage,
      image2: groupImg
    },
    {
      id: 'adhd',
      name: 'ADHD',
      description: 'Attention-Deficit/Hyperactivity Disorder affects focus, impulse control, and activity levels. We provide specialized care for children, adolescents, and adults.',
      bullets: [
        'Inattentive Type ADHD',
        'Hyperactive-Impulsive Type',
        'Combined Type ADHD',
        'Adult ADHD Management'
      ],
      image1: babyImg,
      image2: mentalhealthImg
    },
    {
      id: 'ptsd',
      name: 'PTSD',
      description: 'Post-Traumatic Stress Disorder can develop after experiencing or witnessing a traumatic event. We offer trauma-informed, evidence-based treatment approaches.',
      bullets: [
        'Trauma Assessment & Diagnosis',
        'Evidence-Based Therapies',
        'Medication Management',
        'Coping Strategy Development'
      ],
      image1: mentalhealthImg,
      image2: groupImg
    },
    {
      id: 'bipolar',
      name: 'Bipolar & Mood Disorders',
      description: 'Bipolar disorders involve episodes of mood swings ranging from depressive lows to manic highs. We provide comprehensive mood stabilization and management.',
      bullets: [
        'Bipolar I Disorder',
        'Bipolar II Disorder',
        'Cyclothymic Disorder',
        'Mood Stabilization'
      ],
      image1: mentalhealthImg,
      image2: bikingImg
    }
  ];

  const services = [
    { title: "Initial Psychiatric Evaluation (Intake)", price: "$300", desc: "60‑minute comprehensive evaluation. Personalized treatment plan." },
    { title: "Follow‑Up Medication Management", price: "$150", desc: "20–30 minute progress review and adjustments." },
  ];

  const insurers = ["Aetna", "Evernorth", "Horizon BCBS", "United Healthcare"];

  const faqs = [
    {
      q: "How do I reach the practice?",
      a: (
        <>
          <p>You can reach Insightful Care Solutions in any of these ways:</p>
          <ul>
            <li>
              <button
                type="button"
                onClick={() => AnchorToWithState("contact", setActiveSection)}
                className="text-left text-neutral-800 font-medium underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800 rounded"
              >
                Contact form
              </button>
              {" "}(this page — Contact section)
            </li>
            <li>
              Email:{" "}
              <a href="mailto:info@insightfulcare.solutions" className="break-all">
                info@insightfulcare.solutions
              </a>
            </li>
            <li>
              Phone:{" "}
              <a href={`tel:${BUSINESS_PHONE_E164}`} className="tabular-nums">
                {BUSINESS_PHONE_DISPLAY}
              </a>
            </li>
            <li>
              Instagram:{" "}
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                @insightfulcaresolutions
              </a>
            </li>
          </ul>
          <p>
            Our phone line is for <strong>non‑emergency</strong> scheduling and general questions only. If you are in crisis or need immediate help, call{" "}
            <a href="tel:911">911</a>, call or text{" "}
            <a href="tel:988">988</a>, or go to the nearest emergency department. See the{" "}
            <button
              type="button"
              onClick={() => AnchorToWithState("resources", setActiveSection)}
              className="text-neutral-800 font-medium underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800 rounded"
            >
              Resources
            </button>
            {" "}section for county crisis hotlines.
          </p>
        </>
      ),
    },
    { 
      q: "What should I expect with a telehealth appointment?", 
      a: "Once you have chosen a provider and your appointment is confirmed you will receive a secure HIPAA compliant Zoom link. Attached to that link there will be new patient documents and consent forms that you will be asked to complete before you can start the appointment. We ask that you download Zoom ahead of time to prepare for your appointment and avoid any delays prior to your session." 
    },
    { 
      q: "How will I get my medications?", 
      a: "Your prescriptions will be sent electronically to the pharmacy of your choice." 
    },
    { 
      q: "What ages do you treat?", 
      a: "We currently treat Children, Adolescents, and Adults starting from ages 10 and up" 
    },
    { 
      q: "If I don't have insurance can I still book an appointment?", 
      a: "At this time we accept 4 major commercial insurances including Aetna, Evernorth, Horizon BCBS, and United Healthcare and we also accept private pay. Initial evaluations are $300 and follow up visits are $150, we accept all major credit cards. It's always best practice to check with your insurance for specific details regarding telehealth coverage." 
    },
    { 
      q: "What if I can't make my appointment?", 
      a: "We ask that you cancel or reschedule any appointments within a 48 hour time frame. No shows and late cancellations will be subjected to a fee of $100." 
    },
    { 
      q: "What to do in case of an emergency?", 
      a: "In case of a life-threatening emergency such as feeling unsafe, suicidal thoughts, or homicidal thoughts please call 911 or 988 and go to your nearest hospital to get evaluated. See resources tab for a 24 hour crisis hot line list by county." 
    },
  ];

  const countyHotlines = [
    { county: "ATLANTIC",   facility: "",                                    phone: "(609) 344-1118" },
    { county: "BERGEN",     facility: "",                                    phone: "(201) 262-4357" },
    { county: "BURLINGTON", facility: "",                                    phone: "(609) 835-6180" },
    { county: "CAMDEN",     facility: "",                                    phone: "(856) 428-4357" },
    { county: "CAPE MAY",   facility: "",                                    phone: "(609) 465-5999" },
    { county: "CUMBERLAND", facility: "",                                    phone: "(856) 455-5555" },
    { county: "ESSEX",      facility: "Clara Maas Medical",                  phone: "(973) 844-4357" },
    { county: "ESSEX",      facility: "Newark Beth Israel",                  phone: "(973) 926-7444" },
    { county: "ESSEX",      facility: "University Behavioral Healthcare",    phone: "(973) 623-2323" },
    { county: "GLOUCESTER", facility: "",                                    phone: "(856) 845-9100" },
    { county: "HUDSON",     facility: "Jersey City Medical",                 phone: "(866) 367-6023" },
    { county: "HUDSON",     facility: "Palisades Medical",                   phone: "(201) 854-5760" },
    { county: "HUDSON",     facility: "Christ Hospital",                     phone: "(201) 795-8374" },
    { county: "HUDSON",     facility: "Hoboken University Medical",          phone: "(201) 418-2090" },
    { county: "HUNTERDON",  facility: "",                                    phone: "(908) 788-6400" },
    { county: "MERCER",     facility: "",                                    phone: "(609) 396-4357 / (609) 989-7297" },
    { county: "MIDDLESEX",  facility: "University Behavioral Health",        phone: "(732) 235-5700" },
    { county: "MIDDLESEX",  facility: "Raritan Bay Medical",                 phone: "(732) 324-5289" },
    { county: "MONMOUTH",   facility: "Monmouth Medical",                    phone: "(732) 923-6999" },
    { county: "MONMOUTH",   facility: "Centra State Medical",                phone: "(732) 294-2595" },
    { county: "MONMOUTH",   facility: "Jersey Shore University Medical",     phone: "(732) 776-4555" },
    { county: "MONMOUTH",   facility: "Riverview Medical Center",            phone: "(732) 219-5325" },
    { county: "MORRIS",     facility: "Prime Healthcare Saint Clare's",      phone: "(973) 625-6160" },
    { county: "MORRIS",     facility: "Morristown Memorial Hospital",        phone: "(973) 540-0100" },
    { county: "MORRIS",     facility: "Chilton Medical Center",              phone: "(973) 831-5078" },
    { county: "OCEAN",      facility: "",                                    phone: "(866) 904-4474 / (732) 886-4474" },
    { county: "PASSAIC",    facility: "",                                    phone: "(973) 754-2230" },
    { county: "SALEM",      facility: "",                                    phone: "(856) 299-3001" },
    { county: "SOMERSET",   facility: "",                                    phone: "(908) 526-4100" },
    { county: "SUSSEX",     facility: "",                                    phone: "(973) 383-0973" },
    { county: "UNION",      facility: "Trinitas Regional / RWJ Barnabas",   phone: "(908) 994-7131" },
    { county: "UNION",      facility: "Overlook Medical Center",             phone: "(973) 831-5078" },
    { county: "UNION",      facility: "Rahway Hospital",                     phone: "(732) 381-4949 / (732) 499-6165" },
    { county: "WARREN",     facility: "",                                    phone: "(908) 454-5141" },
  ];

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 pb-28 lg:pb-0" style={{ backgroundColor: 'var(--color-bg-50)' }}>
      {/* HEADER / NAV */}
      <header className="sticky top-0 z-40 border-b border-neutral-200 bg-white/90 backdrop-blur" role="banner" style={{ minHeight: '80px', height: 'auto' }}>
        <Container 
          className="flex items-center justify-between h-full py-2 sm:py-3" 
        >
          <div className="flex items-center justify-start w-auto h-full flex-shrink-0">
            <img 
              src={currentLogoSource} 
              alt="Insightful Care Solutions Logo" 
              className="object-contain block" 
              style={{ 
                height: `${logoSize}px`, 
                width: `${logoSize}px`,
                maxHeight: '100%',
                maxWidth: '100%',
                margin: 0,
                padding: 0
              }}
            />
          </div>
          <nav className="hidden lg:flex items-center justify-center gap-4 xl:gap-6 flex-1" aria-label="Primary">
            {[
              ["home", "Home"],
              ["mission", "Mission"],
              ["services", "Services"],
              ["founders", "Our Team"],
              ["faq", "FAQ"],
              ["resources", "Resources"],
              ["contact", "Contact"],
            ].map(([id, label]) => (
              <NavLink 
                key={id} 
                onClick={() => AnchorToWithState(id, setActiveSection)}
                isActive={activeSection === id}
              >
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 justify-self-end flex-shrink-0">
            <BusinessPhoneLink
              iconOnly
              className="hidden lg:inline-flex min-h-[44px] min-w-[44px] border border-neutral-300 px-2.5 py-2"
              iconClassName="h-5 w-5"
            />
            <InstagramLink href={INSTAGRAM_URL} compact className="hidden lg:inline-flex" />
            <button onClick={() => openExternalBooking()} className="hidden lg:inline-flex rounded-xl bg-neutral-900 hover:bg-neutral-700 focus:outline-none text-white px-4 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap">
              Book Consultation
            </button>
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
              className="lg:hidden p-2 rounded-lg hover:bg-neutral-100 active:bg-neutral-200 focus:bg-neutral-100 focus:outline-none transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </Container>
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-neutral-200 bg-white py-4 max-h-[calc(100vh-80px)] overflow-y-auto">
            <Container>
              <nav className="flex flex-col gap-2">
                {[
                  ["home", "Home"],
                  ["mission", "Mission"],
                  ["services", "Services"],
                  ["founders", "Our Team"],
                  ["faq", "FAQ"],
                  ["resources", "Resources"],
                  ["contact", "Contact"],
                ].map(([id, label]) => (
                  <button 
                    key={id} 
                    onClick={() => {
                      AnchorToWithState(id, setActiveSection);
                      setIsMobileMenuOpen(false);
                    }} 
                    className={`px-4 py-3 text-left text-base rounded-lg transition-colors focus:outline-none min-h-[44px] ${
                      activeSection === id 
                        ? 'bg-neutral-100 text-neutral-900 font-medium' 
                        : 'hover:bg-neutral-100 active:bg-neutral-200 text-neutral-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
                <a
                  href={`tel:${BUSINESS_PHONE_E164}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white px-4 py-3 text-base font-medium text-neutral-800 transition-colors hover:bg-neutral-50 min-h-[44px]"
                  aria-label={`Call Insightful Care Solutions at ${BUSINESS_PHONE_DISPLAY}`}
                >
                  <PhoneIcon className="h-5 w-5 flex-shrink-0" />
                  <span className="tabular-nums">{BUSINESS_PHONE_DISPLAY}</span>
                </a>
                <button 
                  onClick={() => {
                    openExternalBooking();
                    setIsMobileMenuOpen(false);
                  }} 
                  className="mt-2 rounded-xl bg-neutral-900 hover:bg-neutral-700 focus:outline-none text-white px-4 py-3 text-base font-medium transition-colors duration-200 min-h-[44px]"
                >
                  Book Consultation
                </button>
                <InstagramLink href={INSTAGRAM_URL} className="mt-2 justify-center text-base" />
              </nav>
            </Container>
          </div>
        )}
      </header>

      {/* HERO */}
      <Section id="home" title={null} subtitle={null} tone="light" className="relative min-h-[400px] sm:min-h-[500px] md:min-h-[600px]">
        {/* BACKGROUND IMAGE */}
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src={landImg} 
            alt="Calm outdoor landscape representing mental wellness and telepsychiatry care in New Jersey" 
            className="h-full w-full object-cover opacity-60"
            style={{ objectPosition: 'center' }}
          />
        </div>
        <Container>
          <div className="relative max-w-3xl py-8 sm:py-12 md:py-16 lg:py-24">
            <div className="bg-white bg-opacity-50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg">
              <div className="space-y-4 sm:space-y-5">
                <p className="text-sm sm:text-base font-semibold tracking-wide text-neutral-800 uppercase">Insightful Care Solutions</p>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight break-words">Your Mental Health Advocates</h1>
                <p className="text-neutral-700 text-sm sm:text-base md:text-lg leading-relaxed">
                  Clinician‑founded telepsychiatry across New Jersey dedicated to patient‑specific solutions. True healing starts with listening — and we're here to listen to you.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button onClick={() => openExternalBooking()} className="w-full sm:w-auto rounded-xl bg-neutral-900 hover:bg-neutral-700 focus:outline-none text-white px-5 py-3 text-sm sm:text-base font-medium transition-colors duration-200 min-h-[44px]">Book a Consultation</button>
                  <button onClick={() => AnchorTo("services")} className="w-full sm:w-auto rounded-xl border border-neutral-300 hover:bg-neutral-800 hover:text-white hover:border-neutral-800 focus:outline-none px-5 py-3 text-sm sm:text-base font-medium transition-colors duration-200 min-h-[44px]">Explore Services</button>
                </div>
                {/* CONDITION PILLS */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-2 sm:gap-3 pt-4 sm:pt-6">
                  {conditions.map((c) => (
                    <Pill key={c}>{c}</Pill>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* MISSION */}
      <Section id="mission" title="Our Mission" tone="white">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
          <Card className="order-2 lg:order-1">
            <h3 className="text-base sm:text-lg font-semibold text-neutral-600 mb-4">Compassionate Care Empowers Wellness</h3>
            <p className="text-sm sm:text-base text-neutral-700 leading-relaxed">
              Inclusive, compassionate, culturally centered psychiatric care to empower mental wellness and emotional balance for all. Evidence‑based treatment, collaboration, and empathy to remove barriers, reduce stigma, and help individuals rediscover hope and resilience.
            </p>
            <blockquote className="mt-6 text-base sm:text-lg md:text-xl font-bold text-neutral-900 italic text-left break-words">
              "Healing takes time, but it is possible"
            </blockquote>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button onClick={() => AnchorTo("founders")} className="w-full sm:w-auto rounded-lg bg-neutral-900 hover:bg-neutral-700 focus:outline-none text-white px-4 py-2.5 text-sm sm:text-base transition-colors duration-200 min-h-[44px]">Meet Our Team</button>
              <button onClick={() => AnchorTo("services")} className="w-full sm:w-auto rounded-lg border border-neutral-300 hover:bg-neutral-800 hover:text-white hover:border-neutral-800 focus:outline-none px-4 py-2.5 text-sm sm:text-base transition-colors duration-200 min-h-[44px]">View Services</button>
            </div>
            <p className="mt-4 text-xs sm:text-sm text-neutral-600 leading-relaxed">
              Rather talk first? Call us at{" "}
              <a
                href={`tel:${BUSINESS_PHONE_E164}`}
                className="font-semibold text-neutral-800 tabular-nums underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800 rounded"
              >
                {BUSINESS_PHONE_DISPLAY}
              </a>
              {" "}or visit{" "}
              <button
                type="button"
                onClick={() => AnchorToWithState("contact", setActiveSection)}
                className="font-semibold text-neutral-800 underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800 rounded"
              >
                Contact
              </button>
              .
            </p>
          </Card>
          <Card className="order-1 lg:order-2">
            {/* MAIN IMAGE */}
            <img 
              src={mentalhealthImg} 
              alt="Mental Health Care" 
              className="h-48 sm:h-56 md:h-64 lg:h-72 w-full rounded-xl object-cover"
            />
            <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
              <img 
                src={bikingImg} 
                alt="Active Lifestyle" 
                className="h-20 sm:h-24 md:h-32 w-full rounded object-cover"
              />
              <img 
                src={groupImg} 
                alt="Community Support" 
                className="h-20 sm:h-24 md:h-32 w-full rounded object-cover"
              />
              <img 
                src={babyImg} 
                alt="Family Care" 
                className="h-20 sm:h-24 md:h-32 w-full rounded object-cover"
              />
            </div>
          </Card>
        </div>
      </Section>

      {/* SERVICES */}
      <Section id="services" title="Our Services" subtitle="Expert psychiatric evaluations, diagnosis & treatment, and medication management for ages 10+." tone="light">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col gap-1.5 text-xs sm:text-sm text-neutral-600">
            <span>Payments: Online • Virtual‑only practice</span>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-neutral-500">Questions?</span>
              <a
                href={`tel:${BUSINESS_PHONE_E164}`}
                className="inline-flex items-center gap-1.5 font-medium text-neutral-800 tabular-nums underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800 rounded"
              >
                <PhoneIcon className="h-3.5 w-3.5 text-neutral-600 flex-shrink-0" aria-hidden />
                {BUSINESS_PHONE_DISPLAY}
              </a>
            </span>
          </div>
          <div className="flex gap-2 text-xs sm:text-sm text-neutral-600 flex-wrap">
            {insurers.map((i) => (
              <Pill key={i}>Insured: {i}</Pill>
            ))}
          </div>
        </div>
        
        {/* Service Cards */}
        <div className="mt-8 grid md:grid-cols-2 gap-6 mb-12">
          <Card>
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <img 
                src={initialImg} 
                alt="Initial Psychiatric Evaluation" 
                className="h-40 w-full sm:h-32 sm:w-32 md:h-36 md:w-36 rounded-xl object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold break-words">Initial Psychiatric Evaluation (Intake)</h3>
                <div className="mt-1 inline-flex items-center gap-2 rounded-full bg-neutral-100 px-3 py-1 text-xs sm:text-sm">$300</div>
                <p className="mt-3 text-sm sm:text-base text-neutral-700 leading-relaxed">60‑minute comprehensive evaluation. Personalized treatment plan.</p>
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <img 
                src={followupImg} 
                alt="Follow-Up Medication Management" 
                className="h-40 w-full sm:h-32 sm:w-32 md:h-36 md:w-36 rounded-xl object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold break-words">Follow‑Up Medication Management</h3>
                <div className="mt-1 inline-flex items-center gap-2 rounded-full bg-neutral-100 px-3 py-1 text-xs sm:text-sm">$150</div>
                <p className="mt-3 text-sm sm:text-base text-neutral-700 leading-relaxed">20–30 minute progress review and adjustments.</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Disorders Section */}
        <div className="mt-8 space-y-3">
          <h3 className="text-lg sm:text-xl font-semibold text-neutral-900 mb-4">Conditions We Treat</h3>
          {disorders.map((disorder) => (
            <div key={disorder.id} className="border border-neutral-300 rounded-xl bg-white overflow-hidden">
              {/* Disorder Bar */}
              <button
                onClick={() => setOpenDisorder(openDisorder === disorder.id ? null : disorder.id)}
                className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-neutral-50 active:bg-neutral-100 transition-colors duration-200 text-left focus:outline-none focus:ring-2 focus:ring-neutral-800 focus:ring-offset-2 min-h-[44px]"
                aria-expanded={openDisorder === disorder.id}
              >
                <h4 className="text-base sm:text-lg font-semibold text-neutral-900 break-words pr-2">{disorder.name}</h4>
                <svg
                  className={`w-5 h-5 sm:w-6 sm:h-6 text-neutral-600 transition-transform duration-200 flex-shrink-0 ${
                    openDisorder === disorder.id ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Content */}
              {openDisorder === disorder.id && (
                <div className="border-t border-neutral-200 p-4 sm:p-5 bg-neutral-50">
                  <div className="grid lg:grid-cols-2 gap-6">
                    {/* Left Column: Description and Bullets */}
                    <div className="space-y-4 order-2 lg:order-1">
                      <p className="text-sm sm:text-base text-neutral-700 leading-relaxed break-words">
                        {disorder.description}
                      </p>
                      
                      {/* 2x2 Bullet Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {disorder.bullets.map((bullet, index) => (
                          <div key={index} className="flex items-start">
                            <span className="text-neutral-700 mr-2 mt-1 flex-shrink-0">◆</span>
                            <span className="text-sm sm:text-base text-neutral-700 break-words">{bullet}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right Column: Images/Videos */}
                    <div className="space-y-4 order-1 lg:order-2">
                      <div className="relative w-full h-48 sm:h-64 md:h-80 lg:h-96 rounded-xl overflow-hidden bg-neutral-200">
                        <img 
                          src={disorder.image1} 
                          alt={`${disorder.name} - Image 1`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="relative w-full h-40 sm:h-48 md:h-56 rounded-xl overflow-hidden bg-neutral-200">
                        <img 
                          src={disorder.image2} 
                          alt={`${disorder.name} - Image 2`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* OUR TEAM — overview strip */}
      <Section id="founders" title="Meet Our Team" subtitle="Clinician‑founded, culturally centered, and deeply committed to your wellbeing." tone="white">

        <div className="mb-8 sm:mb-10">
          <FounderAvailability />
        </div>

        {/* Team preview cards */}
        <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">

          {/* Keyshanna card */}
          <div
            className="group relative flex flex-col items-center rounded-2xl border border-neutral-200 bg-neutral-50 overflow-hidden hover:border-neutral-400 hover:shadow-lg transition-all duration-300 cursor-pointer"
            onClick={() => AnchorTo("team-keyshanna")}
          >
            {/* Gradient accent bar */}
            <div className="w-full h-1.5 bg-gradient-to-r from-neutral-700 via-neutral-500 to-neutral-300" />
            <div className="flex flex-col items-center px-6 pt-8 pb-6 w-full">
              <div className="relative w-40 h-40 sm:w-48 sm:h-48 rounded-full overflow-hidden border-4 border-white shadow-md mb-4 flex-shrink-0 bg-neutral-100">
                <img
                  src={keyshannaTeamCircleImg}
                  alt="Keyshanna Apple, MSN, APRN, PMHNP‑BC"
                  className="h-full w-full object-contain object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 text-center">Keyshanna Apple</h3>
              <p className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mt-1 text-center">MSN, APRN, PMHNP‑BC</p>
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-neutral-900 text-white text-xs font-medium px-3 py-1">
                Co‑Founder · PMHNP
              </span>
              <p className="mt-4 text-sm text-neutral-600 text-center leading-relaxed max-w-xs">
                Compassionate Patient centered care with a culturally-rooted perspective dedicated to reducing stigma and empowering you for long term wellness.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-2 w-full">
                <button
                  onClick={(e) => { e.stopPropagation(); AnchorTo("team-keyshanna"); }}
                  className="flex-1 rounded-xl border border-neutral-300 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 focus:outline-none px-4 py-2.5 text-sm font-medium text-neutral-800 transition-colors duration-200 min-h-[44px]"
                >
                  View Profile
                </button>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener"
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 flex items-center justify-center rounded-xl bg-neutral-900 hover:bg-neutral-700 focus:outline-none text-white px-4 py-2.5 text-sm font-medium transition-colors duration-200 min-h-[44px]"
                >
                  Book Now
                </a>
              </div>
            </div>
          </div>

          {/* Linda card */}
          <div
            className="group relative flex flex-col items-center rounded-2xl border border-neutral-200 bg-neutral-50 overflow-hidden hover:border-neutral-400 hover:shadow-lg transition-all duration-300 cursor-pointer"
            onClick={() => AnchorTo("team-linda")}
          >
            <div className="w-full h-1.5 bg-gradient-to-r from-neutral-300 via-neutral-500 to-neutral-700" />
            <div className="flex flex-col items-center px-6 pt-8 pb-6 w-full">
              <div className="relative w-40 h-40 sm:w-48 sm:h-48 rounded-full overflow-hidden border-4 border-white shadow-md mb-4 flex-shrink-0 bg-neutral-100">
                <img
                  src={lindaTeamCircleImg}
                  alt="Linda Agyeiwaa, MSN, APRN, PMHNP‑BC"
                  className="h-full w-full object-contain object-center group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 text-center">Linda Agyeiwaa</h3>
              <p className="text-xs font-semibold tracking-widest uppercase text-neutral-500 mt-1 text-center">MSN, APRN, PMHNP‑BC</p>
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-neutral-900 text-white text-xs font-medium px-3 py-1">
                Co‑Founder · PMHNP
              </span>
              <p className="mt-4 text-sm text-neutral-600 text-center leading-relaxed max-w-xs">
                Holistic &amp; evidence‑based care that bridges traditional and alternative treatment — empowering you to lead your own recovery.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-2 w-full">
                <button
                  onClick={(e) => { e.stopPropagation(); AnchorTo("team-linda"); }}
                  className="flex-1 rounded-xl border border-neutral-300 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 focus:outline-none px-4 py-2.5 text-sm font-medium text-neutral-800 transition-colors duration-200 min-h-[44px]"
                >
                  View Profile
                </button>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener"
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 flex items-center justify-center rounded-xl bg-neutral-900 hover:bg-neutral-700 focus:outline-none text-white px-4 py-2.5 text-sm font-medium transition-colors duration-200 min-h-[44px]"
                >
                  Book Now
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* "Here to help" band */}
        <div className="mt-10 rounded-2xl bg-neutral-900 text-white p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="text-lg sm:text-xl font-bold">Ready to take the first step?</h3>
            <p className="mt-1 text-sm text-neutral-300 max-w-md">We understand that seeking help is a big step. Our caring team is here to support you every step of the way.</p>
          </div>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener"
            className="flex-shrink-0 rounded-xl border border-white hover:bg-white hover:text-neutral-900 focus:outline-none text-white px-6 py-3 text-sm font-semibold transition-colors duration-200 min-h-[44px] flex items-center"
          >
            Schedule a Consultation
          </a>
        </div>
      </Section>

      {/* OUR TEAM — Keyshanna full profile */}
      <section id="team-keyshanna" aria-label="Keyshanna Apple Profile" className="bg-neutral-50 scroll-mt-24 py-10 sm:py-14 lg:py-20 border-t border-neutral-100">
        <Container>
          {/* Back link */}
          <button
            onClick={() => AnchorTo("founders")}
            className="mb-8 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 transition-colors focus:outline-none group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Our Team
          </button>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">

            {/* Left column — photo + booking CTA */}
            <div className="lg:col-span-2 flex flex-col items-center lg:items-start gap-5 lg:sticky lg:top-28">
              <div className="relative w-full max-w-sm mx-auto lg:mx-0">
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-neutral-300 to-neutral-100 blur-sm" aria-hidden />
                <img
                  src={keyshannaProfileImg}
                  alt="Keyshanna Apple, MSN, APRN, PMHNP‑BC"
                  className="relative w-full max-w-sm aspect-square rounded-2xl object-cover object-center shadow-md"
                />
              </div>
              {/* Name / credentials badge */}
              <div className="w-full max-w-sm mx-auto lg:mx-0 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
                <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 leading-tight">Keyshanna<br />Apple</h2>
                <p className="mt-1 text-sm font-semibold tracking-widest uppercase text-neutral-400">MSN, APRN, PMHNP‑BC</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-full bg-neutral-900 text-white text-xs font-medium px-3 py-1">Co‑Founder</span>
                  <span className="rounded-full border border-neutral-300 text-neutral-700 text-xs font-medium px-3 py-1">PMHNP</span>
                  <span className="rounded-full border border-neutral-300 text-neutral-700 text-xs font-medium px-3 py-1">Telehealth</span>
                </div>
                <div className="mt-5 space-y-2.5">
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener"
                    className="w-full flex items-center justify-center gap-2 rounded-xl bg-neutral-900 hover:bg-neutral-700 focus:outline-none text-white px-4 py-3 text-sm font-semibold transition-colors duration-200 min-h-[44px]"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Book Now — ChARM Health
                  </a>
                  <button
                    onClick={() => AnchorTo("contact")}
                    className="w-full rounded-xl border border-neutral-300 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 focus:outline-none text-neutral-800 px-4 py-3 text-sm font-medium transition-colors duration-200 min-h-[44px]"
                  >
                    Send a Message
                  </button>
                </div>
                <div className="mt-4">
                  <FounderAvailability compact />
                </div>
                <p className="mt-3 text-xs text-neutral-400 text-center leading-relaxed">
                  Scheduling via ChARM Health — HIPAA‑compliant
                </p>
              </div>
            </div>

            {/* Right column — full bio */}
            <div className="lg:col-span-3 space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900">Meet Keyshanna Apple</h2>
                <div className="mt-1 h-1 w-16 rounded-full bg-neutral-800" />
                <p className="mt-5 text-sm sm:text-base text-neutral-700 leading-relaxed">
                  As a Board Certified Psychiatric Mental Health Nurse Practitioner dedicated to providing care to individuals across the lifespan. Throughout my career I've had the pleasure of helping patients achieve optimal wellness in the critical care and mental health setting. As a woman of Caribbean heritage I have a huge respect for cultural competency with an understanding that each patient's unique background helps to shape who they are.
                </p>
                <p className="mt-4 text-sm sm:text-base text-neutral-700 leading-relaxed">
                  I strive to provide patient centered care to patients dealing with a variety of mental health challenges ranging from but not limited to Anxiety, Depression, OCD, PTSD, Mood disorders and other psychiatric disorders. I want to ensure that each patient feels heard and has the access and necessary tools to improve their quality of life.
                </p>
              </div>

              {/* What it's like section */}
              <div className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6 shadow-sm">
                <h3 className="text-base sm:text-lg font-semibold text-neutral-900 mb-4">What it's like to work with me</h3>
                <ul className="space-y-3">
                  {[
                    "Supportive, empowering, and educational approach",
                    "Building rapport and creating safe spaces",
                    "Empathy and judgment-free environment",
                    "Collaborative treatment planning",
                    "Helping you regain control and maximize wellbeing",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white text-xs" aria-hidden>✓</span>
                      <span className="text-sm sm:text-base text-neutral-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Typical process */}
              <div className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6 shadow-sm">
                <h3 className="text-base sm:text-lg font-semibold text-neutral-900 mb-5">Your care journey</h3>
                <div className="space-y-0">
                  {[
                    "Providing a safe space to establish therapeutic rapport — learning your concerns and goals for treatment",
                    "Thorough evaluation exploring prior diagnoses, medication and psychosocial history",
                    "Collaborative development of your personalized treatment plan",
                    "Evidence-based practice addressing your specific concerns",
                    "Medication management and supportive therapy",
                    "Lifestyle interventions for complete wellness and mental health restoration",
                    "Guided support with open communication and follow-up visits",
                  ].map((step, i, arr) => (
                    <div key={i}>
                      <div className="flex items-start gap-4 py-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-neutral-900 text-white flex items-center justify-center text-xs font-bold">{i + 1}</div>
                        <p className="text-sm sm:text-base text-neutral-700 leading-relaxed">{step}</p>
                      </div>
                      {i < arr.length - 1 && <div className="ml-4 w-px h-4 bg-neutral-200" />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom CTA */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener"
                  className="flex-1 flex items-center justify-center rounded-xl bg-neutral-900 hover:bg-neutral-700 focus:outline-none text-white px-5 py-3 text-sm sm:text-base font-semibold transition-colors duration-200 min-h-[44px]"
                >
                  Book with Keyshanna
                </a>
                <button
                  onClick={() => AnchorTo("team-linda")}
                  className="flex-1 rounded-xl border border-neutral-300 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 focus:outline-none px-5 py-3 text-sm sm:text-base font-medium transition-colors duration-200 min-h-[44px]"
                >
                  Meet Linda →
                </button>
              </div>
            </div>

          </div>
        </Container>
      </section>

      {/* OUR TEAM — Linda full profile */}
      <section id="team-linda" aria-label="Linda Agyeiwaa Profile" className="bg-white scroll-mt-24 py-10 sm:py-14 lg:py-20 border-t border-neutral-100">
        <Container>
          <button
            onClick={() => AnchorTo("founders")}
            className="mb-8 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 transition-colors focus:outline-none group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Our Team
          </button>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">

            {/* Left column */}
            <div className="lg:col-span-2 flex flex-col items-center lg:items-start gap-5 lg:sticky lg:top-28">
              <div className="relative w-full max-w-sm mx-auto lg:mx-0">
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-neutral-200 to-neutral-100 blur-sm" aria-hidden />
                <img
                  src={lindaProfileImg}
                  alt="Linda Agyeiwaa, MSN, APRN, PMHNP‑BC"
                  className="relative w-full max-w-sm aspect-square rounded-2xl object-cover object-center shadow-md"
                />
              </div>
              <div className="w-full max-w-sm mx-auto lg:mx-0 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
                <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 leading-tight">Linda<br />Agyeiwaa</h2>
                <p className="mt-1 text-sm font-semibold tracking-widest uppercase text-neutral-400">MSN, APRN, PMHNP‑BC</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-full bg-neutral-900 text-white text-xs font-medium px-3 py-1">Co‑Founder</span>
                  <span className="rounded-full border border-neutral-300 text-neutral-700 text-xs font-medium px-3 py-1">PMHNP</span>
                  <span className="rounded-full border border-neutral-300 text-neutral-700 text-xs font-medium px-3 py-1">Telehealth</span>
                </div>
                <div className="mt-5 space-y-2.5">
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener"
                    className="w-full flex items-center justify-center gap-2 rounded-xl bg-neutral-900 hover:bg-neutral-700 focus:outline-none text-white px-4 py-3 text-sm font-semibold transition-colors duration-200 min-h-[44px]"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Book Now — ChARM Health
                  </a>
                  <button
                    onClick={() => AnchorTo("contact")}
                    className="w-full rounded-xl border border-neutral-300 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 focus:outline-none text-neutral-800 px-4 py-3 text-sm font-medium transition-colors duration-200 min-h-[44px]"
                  >
                    Send a Message
                  </button>
                </div>
                <div className="mt-4">
                  <FounderAvailability compact />
                </div>
                <p className="mt-3 text-xs text-neutral-400 text-center leading-relaxed">
                  Scheduling via ChARM Health — HIPAA‑compliant
                </p>
              </div>
            </div>

            {/* Right column */}
            <div className="lg:col-span-3 space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900">Meet Linda Agyeiwaa</h2>
                <div className="mt-1 h-1 w-16 rounded-full bg-neutral-800" />
                <p className="mt-5 text-sm sm:text-base text-neutral-700 leading-relaxed">
                  As a Board-Certified Psychiatric Mental Health Nurse Practitioner, I am deeply committed to providing compassionate, comprehensive care to individuals facing mental health challenges. My goal is to not only offer effective treatment but also to help break down the stigma surrounding mental health, creating a supportive environment where patients feel safe and understood.
                </p>
                <p className="mt-4 text-sm sm:text-base text-neutral-700 leading-relaxed">
                  I strive to integrate both traditional and alternative methods of care, empowering clients to take an active role in their mental health journey and achieve their overall wellness goals.
                </p>
              </div>

              <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5 sm:p-6 shadow-sm">
                <h3 className="text-base sm:text-lg font-semibold text-neutral-900 mb-4">What it's like to work with me</h3>
                <ul className="space-y-3">
                  {[
                    "Compassionate care across the lifespan",
                    "Breaking down mental health stigmas",
                    "Diverse experience in medical-surgical and intensive care",
                    "Holistic approach addressing mental, emotional, and physical wellbeing",
                    "Personalized, patient-centered care",
                    "Empowering individuals to achieve wellness goals",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white text-xs" aria-hidden>✓</span>
                      <span className="text-sm sm:text-base text-neutral-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5 sm:p-6 shadow-sm">
                <h3 className="text-base sm:text-lg font-semibold text-neutral-900 mb-5">Your care journey</h3>
                <div className="space-y-0">
                  {[
                    "Creating a welcoming, non-judgmental space for safe and open expression",
                    "Thorough assessment including medical, psychiatric, and psychosocial histories",
                    "Understanding your unique experiences and challenges",
                    "Collaborative development of your personalized treatment plan",
                    "Combination of psychotherapy, medication management, and lifestyle modifications",
                    "Holistic care addressing both mental and physical health",
                    "Open communication and breaking down mental health barriers together",
                  ].map((step, i, arr) => (
                    <div key={i}>
                      <div className="flex items-start gap-4 py-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-neutral-900 text-white flex items-center justify-center text-xs font-bold">{i + 1}</div>
                        <p className="text-sm sm:text-base text-neutral-700 leading-relaxed">{step}</p>
                      </div>
                      {i < arr.length - 1 && <div className="ml-4 w-px h-4 bg-neutral-200" />}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener"
                  className="flex-1 flex items-center justify-center rounded-xl bg-neutral-900 hover:bg-neutral-700 focus:outline-none text-white px-5 py-3 text-sm sm:text-base font-semibold transition-colors duration-200 min-h-[44px]"
                >
                  Book with Linda
                </a>
                <button
                  onClick={() => AnchorTo("team-keyshanna")}
                  className="flex-1 rounded-xl border border-neutral-300 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 focus:outline-none px-5 py-3 text-sm sm:text-base font-medium transition-colors duration-200 min-h-[44px]"
                >
                  ← Meet Keyshanna
                </button>
              </div>
            </div>

          </div>
        </Container>
      </section>

      {/* REVIEWS (temporarily hidden, kept for future use) */}
      {false && (
        <Section id="reviews" title="What Our Clients Say" subtitle="Real stories of support, recovery, and resilience." tone="light">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <div className="flex items-start gap-3 sm:gap-4">
                <img src={hs1Img} alt="Client testimonial" className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm sm:text-base font-medium text-neutral-900 break-words">Sarah Johnson</div>
                  <div className="text-xs sm:text-sm text-neutral-600">Age 28</div>
                </div>
              </div>
              <p className="mt-3 text-sm sm:text-base text-neutral-700 italic leading-relaxed break-words">"Dr. Apple helped me manage my anxiety in ways I never thought possible. Her approach is so compassionate and understanding. I finally feel like I have control over my mental health again."</p>
            </Card>
            <Card>
              <div className="flex items-start gap-3 sm:gap-4">
                <img src={hs2Img} alt="Client testimonial" className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm sm:text-base font-medium text-neutral-900 break-words">Michael Chen</div>
                  <div className="text-xs sm:text-sm text-neutral-600">Age 34</div>
                </div>
              </div>
              <p className="mt-3 text-sm sm:text-base text-neutral-700 italic leading-relaxed break-words">"Working with Dr. Agyeiwaa has been life-changing. She truly listens and creates a safe space where I can be vulnerable. Her holistic approach helped me overcome depression and find hope again."</p>
            </Card>
            <Card className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-start gap-3 sm:gap-4">
                <img src={hs3Img} alt="Client testimonial" className="h-20 w-20 sm:h-24 sm:w-24 rounded-full object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm sm:text-base font-medium text-neutral-900 break-words">Emily Rodriguez</div>
                  <div className="text-xs sm:text-sm text-neutral-600">Age 22</div>
                </div>
              </div>
              <p className="mt-3 text-sm sm:text-base text-neutral-700 italic leading-relaxed break-words">"The telehealth experience was seamless and comfortable. Both providers made me feel heard and supported throughout my journey. I'm grateful for their expertise and genuine care for my wellbeing."</p>
            </Card>
          </div>
          <div className="mt-6 flex justify-center">
            <button onClick={() => openExternalBooking()} className="w-full sm:w-auto rounded-xl bg-neutral-900 hover:bg-neutral-800 focus:outline-none text-white px-5 py-3 text-sm sm:text-base transition-colors duration-200 min-h-[44px]">Book Your Consultation</button>
          </div>
        </Section>
      )}

      {/* FAQ */}
      <Section id="faq" title="Frequently Asked Questions" tone="white">
        <Card>
          {faqs.map((f) => (
            <FAQItem key={f.q} q={f.q} a={f.a} />
          ))}
        </Card>
      </Section>

      {/* RESOURCES */}
      <Section id="resources" title="Mental Health Resources" subtitle="You are not alone. Help is available 24 hours a day, 7 days a week — please reach out." tone="light">
        {/* Emergency Hotlines */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-red-200 bg-red-50 p-5 text-center shadow-sm">
            <span className="text-3xl font-extrabold text-red-600 tracking-tight">911</span>
            <span className="mt-1 text-sm font-semibold text-neutral-800">Emergency Services</span>
            <span className="mt-1 text-xs text-neutral-500">Life-threatening emergencies</span>
            <a href="tel:911" className="mt-3 inline-block rounded-lg bg-red-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-red-700 transition-colors">Call Now</a>
          </div>
          <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-amber-200 bg-amber-50 p-5 text-center shadow-sm">
            <span className="text-2xl font-extrabold text-amber-700 tracking-tight">(855) 654‑6735</span>
            <span className="mt-1 text-sm font-semibold text-neutral-800">NJ Hopeline</span>
            <span className="mt-1 text-xs text-neutral-500">24/7 mental health support</span>
            <a href="tel:8556546735" className="mt-3 inline-block rounded-lg bg-amber-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 transition-colors">Call Now</a>
          </div>
          <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-blue-200 bg-blue-50 p-5 text-center shadow-sm">
            <span className="text-3xl font-extrabold text-blue-600 tracking-tight">988</span>
            <span className="mt-1 text-sm font-semibold text-neutral-800">Suicide &amp; Crisis Lifeline</span>
            <span className="mt-1 text-xs text-neutral-500">Call or text anytime, free &amp; confidential</span>
            <a href="tel:988" className="mt-3 inline-block rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 transition-colors">Call / Text</a>
          </div>
        </div>

        {/* County Hotlines */}
        <Card className="overflow-hidden">
          <button
            onClick={() => setShowCountyHotlines(!showCountyHotlines)}
            className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-neutral-50 focus:outline-none transition-colors"
            aria-expanded={showCountyHotlines}
          >
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-neutral-900">County 24‑Hour Crisis Hotlines</h3>
              <p className="mt-0.5 text-xs sm:text-sm text-neutral-500">For crisis intervention services and psychiatric emergencies across New Jersey</p>
            </div>
            <div className={`ml-4 flex-shrink-0 h-8 w-8 flex items-center justify-center rounded-full bg-neutral-100 transition-transform duration-300 ${showCountyHotlines ? 'rotate-180' : ''}`}>
              <svg className="h-4 w-4 text-neutral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {showCountyHotlines && (
            <div className="border-t border-neutral-100 p-4 sm:p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {countyHotlines.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-xl border border-neutral-100 bg-neutral-50 p-3 hover:bg-neutral-100 transition-colors">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-neutral-800 text-white">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-bold uppercase tracking-wider text-neutral-500">{item.county}</span>
                      {item.facility && <p className="text-xs text-neutral-600 break-words leading-snug">{item.facility}</p>}
                      <p className="mt-0.5 text-sm font-semibold text-neutral-900 break-all">{item.phone}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </Section>

      {/* CONTACT */}
      <Section id="contact" title="We're Here to Listen" subtitle="Fill out the form or call us — we'll follow up with next steps and a secure telehealth link." tone="light">
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="order-2 lg:order-1">
            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:gap-5">
              <Input 
                label="Full Name" 
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Jane Doe" 
                required 
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input 
                  label="Email" 
                  name="email"
                  type="email" 
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@example.com" 
                  required 
                />
                <Input 
                  label="Phone (optional)" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(555) 555‑5555" 
                />
              </div>
              <Select 
                label="Inquiry Type"
                name="inquiryType"
                value={formData.inquiryType}
                onChange={handleInputChange}
              >
                <option value="Service Inquiry">Service Inquiry</option>
                <option value="General Inquiry">General Inquiry</option>
              </Select>
              <TextArea 
                label="Message" 
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="How can we support you?" 
              />
              {submitStatus === 'success' && (
                <div className="p-3 sm:p-4 bg-neutral-100 border border-neutral-400 text-neutral-800 rounded-lg text-sm sm:text-base break-words">
                  Thank you! We've received your message and will follow up with you soon. You should also receive a confirmation email.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-3 sm:p-4 bg-neutral-200 border border-neutral-500 text-neutral-900 rounded-lg text-sm sm:text-base break-words">
                  Sorry, there was an error sending your message. Please try again, email{" "}
                  <span className="break-all font-medium">info@insightfulcare.solutions</span>
                  , or call{" "}
                  <a href={`tel:${BUSINESS_PHONE_E164}`} className="font-medium underline underline-offset-2 tabular-nums hover:no-underline">
                    {BUSINESS_PHONE_DISPLAY}
                  </a>
                  .
                </div>
              )}
              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full sm:w-auto rounded-xl bg-neutral-900 hover:bg-neutral-700 focus:outline-none text-white px-5 py-3 text-sm sm:text-base transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              >
                {isSubmitting ? 'Sending...' : 'Submit'}
              </button>
            </form>
          </Card>
           <Card className="order-1 lg:order-2 lg:min-h-[500px]">
             <h3 className="text-base sm:text-lg font-semibold">Practice Details</h3>
             <dl className="mt-4 sm:mt-6 space-y-3 sm:space-y-4 text-sm sm:text-base text-neutral-700">
               <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                 <dt className="font-medium sm:col-span-1">Location</dt>
                 <dd className="text-xs sm:text-sm sm:col-span-2 break-words">Virtual‑only • 1 Eves Drive, STE 111, Marlton, NJ 08053</dd>
               </div>
               <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                 <dt className="font-medium sm:col-span-1">Payments</dt>
                 <dd className="text-xs sm:text-sm sm:col-span-2 break-words">Online only (major credit cards). Private pay available.</dd>
               </div>
               <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                 <dt className="font-medium sm:col-span-1">Insurance</dt>
                 <dd className="text-xs sm:text-sm sm:col-span-2 break-words">Aetna, Evernorth, Horizon BCBS, United Healthcare</dd>
               </div>
               <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                 <dt className="font-medium sm:col-span-1">Ages</dt>
                 <dd className="text-xs sm:text-sm sm:col-span-2">10 and up</dd>
               </div>
               <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                 <dt className="font-medium sm:col-span-1">Email</dt>
                 <dd className="text-xs sm:text-sm sm:col-span-2 break-all">info@insightfulcare.solutions</dd>
               </div>
               <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                 <dt className="font-medium sm:col-span-1">Phone</dt>
                 <dd className="text-xs sm:text-sm sm:col-span-2">
                   <a
                     href={`tel:${BUSINESS_PHONE_E164}`}
                     className="inline-flex items-center gap-2 font-semibold text-neutral-900 tabular-nums underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800 rounded"
                   >
                     <PhoneIcon className="h-4 w-4 text-neutral-600 flex-shrink-0" aria-hidden />
                     {BUSINESS_PHONE_DISPLAY}
                   </a>
                 </dd>
               </div>
               <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                 <dt className="font-medium sm:col-span-1">Instagram</dt>
                 <dd className="text-xs sm:text-sm sm:col-span-2">
                   <InstagramLink href={INSTAGRAM_URL} className="w-full sm:w-auto justify-center sm:justify-start" />
                 </dd>
               </div>
             </dl>
             <div className="mt-4 sm:mt-6 rounded-lg bg-neutral-100 p-3 sm:p-4 text-xs sm:text-sm text-neutral-800 break-words">
               <strong>Emergency:</strong> Call 911 or 988 or go to the nearest hospital.
             </div>
           </Card>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-neutral-200 bg-white" role="contentinfo">
        <Container className="py-8 sm:py-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          <div className="order-1">
            <div className="flex items-center justify-center sm:justify-start gap-2 sm:gap-3">
              <img 
                src={currentLogoSource} 
                alt="Insightful Care Solutions Logo" 
                className="object-contain" 
                style={{ height: '120px', width: '120px', maxWidth: '100%' }}
              />
            </div>
            <p className="mt-3 text-xs sm:text-sm text-neutral-600 text-center sm:text-left break-words">Inclusive, compassionate, culturally centered telepsychiatry.</p>
          </div>
          <div className="order-2">
            <h4 className="font-semibold text-sm sm:text-base text-center sm:text-left">Navigate</h4>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
              {[
                ["home","Home"],
                ["mission","Mission"],
                ["services","Services"],
                ["founders","Our Team"],
                ["faq","FAQ"],
                ["resources","Resources"],
                ["contact","Contact"],
              ].map(([id, label]) => (
                <button 
                  key={id} 
                  onClick={() => AnchorToWithState(id, setActiveSection)} 
                  className={`focus:outline-none transition-colors duration-200 text-left min-h-[32px] sm:min-h-[36px] ${
                    activeSection === id 
                      ? 'text-neutral-900 font-medium' 
                      : 'hover:text-neutral-800 text-neutral-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="order-3">
            <h4 className="font-semibold text-sm sm:text-base text-center sm:text-left">Services</h4>
            <ul className="mt-3 space-y-2 text-xs sm:text-sm text-neutral-700">
              {services.map((s) => (
                <li key={s.title} className="break-words text-center sm:text-left">{s.title} — <span className="text-neutral-500">{s.price}</span></li>
              ))}
            </ul>
          </div>
          <div className="order-4">
            <h4 className="font-semibold text-sm sm:text-base text-center sm:text-left">Contact</h4>
            <ul className="mt-3 space-y-2 text-xs sm:text-sm text-neutral-700">
              <li className="break-words text-center sm:text-left">Virtual‑only • 1 Eves Drive, STE 111, Marlton, NJ 08053</li>
              <li className="text-center sm:text-left">
                <a href={`tel:${BUSINESS_PHONE_E164}`} className="inline-flex items-center justify-center sm:justify-start gap-1.5 font-medium text-neutral-800 tabular-nums hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800 rounded">
                  <PhoneIcon className="h-3.5 w-3.5 flex-shrink-0 text-neutral-600" aria-hidden />
                  {BUSINESS_PHONE_DISPLAY}
                </a>
              </li>
              <li className="text-center sm:text-left">Email: <span className="break-all">info@insightfulcare.solutions</span></li>
              <li className="break-words text-center sm:text-left">Insurance: {insurers.join(", ")}</li>
              <li className="text-center sm:text-left">Ages 10 and up</li>
            </ul>
            <div className="mt-3 flex justify-center sm:justify-start">
              <InstagramLink href={INSTAGRAM_URL} />
            </div>
          </div>
        </Container>
        <div className="border-t border-neutral-200 py-4 text-center text-xs sm:text-sm text-neutral-500 px-4">© {new Date().getFullYear()} Insightful Care Solutions LLC. All rights reserved.</div>
      </footer>

      <nav
        className="fixed inset-x-0 bottom-0 z-50 border-t border-neutral-200 bg-white/95 shadow-[0_-8px_30px_rgba(0,0,0,0.06)] backdrop-blur-md lg:hidden"
        aria-label="Quick contact and booking"
      >
        <div className="mx-auto flex max-w-7xl gap-2 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <a
            href={`tel:${BUSINESS_PHONE_E164}`}
            className="flex min-h-[52px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-center text-neutral-900 transition-colors hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800 focus-visible:ring-offset-2"
            aria-label={`Call Insightful Care Solutions at ${BUSINESS_PHONE_DISPLAY}`}
          >
            <span className="flex items-center gap-1.5 text-sm font-semibold">
              <PhoneIcon className="h-4 w-4 flex-shrink-0" aria-hidden />
              Call
            </span>
            <span className="text-[11px] font-medium tabular-nums text-neutral-600 leading-none">{BUSINESS_PHONE_DISPLAY}</span>
          </a>
          <button
            type="button"
            onClick={() => {
              setIsMobileMenuOpen(false);
              openExternalBooking();
            }}
            className="flex min-h-[52px] flex-1 items-center justify-center rounded-xl bg-neutral-900 px-4 text-sm font-semibold text-white transition-colors hover:bg-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800 focus-visible:ring-offset-2"
          >
            Book appointment
          </button>
        </div>
      </nav>

      {/* CONTENT MATRIX (JSON) — for Cursor ingestion */}
      <script type="application/json" id="content-matrix">
        {JSON.stringify(CONTENT_MATRIX, null, 2)}
      </script>
    </div>
  );
}

// ----------------------------
// CONTENT MATRIX (replace values programmatically in Cursor)
// ----------------------------
const CONTENT_MATRIX = {
  site: {
    name: "Insightful Care Solutions",
    email: "info@insightfulcare.solutions",
    phone: BUSINESS_PHONE_DISPLAY,
    phoneE164: BUSINESS_PHONE_E164,
    address: "1 Eves Drive, STE 111, Marlton, NJ 08053",
    virtualOnly: true,
    payments: "Online only",
    insurers: ["Aetna", "Evernorth", "Horizon BCBS", "United Healthcare"],
    ages: "10 and up"
  },
  hero: {
    h1: "Your Mental Health Advocates",
    sub: "Clinician‑founded telepsychiatry dedicated to patient‑specific solutions. True healing starts with listening.",
    ctas: [
      { label: "Book a Consultation", to: "#contact", kind: "primary" },
      { label: "Explore Services", to: "#services", kind: "secondary" }
    ],
    conditions: ["Anxiety & Panic","Depression","Bipolar & Mood Disorders","OCD","PTSD","Adjustment Disorder","ADHD"]
  },
  mission: {
    title: "Our Mission",
    body: "At Insightful Care Solutions LLC, our mission is to strengthen the mental health of our community by providing inclusive, compassionate, culturally centered psychiatric care..."
  },
  services: [
    { name: "Initial Psychiatric Evaluation (Intake)", price: 300, unit: "USD", desc: "60‑minute comprehensive evaluation; personalized plan." },
    { name: "Follow‑Up Medication Management", price: 150, unit: "USD", desc: "20–30 minute progress review and adjustments." }
  ],
  founders: [
    {
      name: "Keyshanna Apple, MSN, APRN, PMHNP‑BC",
      role: "Co‑Founder • Psychiatric Mental Health Nurse Practitioner",
      about: "Board‑Certified PMHNP with cultural competency focus; anxiety, depression, OCD, PTSD, mood & psychotic disorders.",
      working: "Supportive, empowering, and informative care in a judgment‑free space.",
      process: "Thorough evaluation → evidence‑based plan (may include meds, therapy, lifestyle) → guided follow‑ups.",
      image: "placeholder://founder-keyshanna"
    },
    {
      name: "Linda Agyeiwaa, MSN, APRN, PMHNP‑BC",
      role: "Co‑Founder • Psychiatric Mental Health Nurse Practitioner",
      about: "Compassionate, comprehensive care; blends traditional and alternative approaches; psychoeducation & coping strategies.",
      working: "Holistic, patient‑centered care across the lifespan.",
      process: "Warm, non‑judgmental assessment → collaborative plan (psychotherapy, meds, lifestyle).",
      image: "placeholder://founder-linda"
    }
  ],
  faq: [
    { q: "How do I reach the practice?", a: "Contact form, email, phone (non-emergency), Instagram; use 911/988 for emergencies." },
    { q: "What should I expect with a telehealth appointment?", a: "Secure HIPAA Zoom link + intake docs; download Zoom in advance." },
    { q: "How will I get my medications?", a: "Sent electronically to your chosen pharmacy." },
    { q: "What ages do you treat?", a: "Ages 10 and up." },
    { q: "If I don't have insurance can I still book?", a: "Yes; private pay accepted ($300 initial; $150 follow‑up)." },
    { q: "What if I can't make my appointment?", a: "Cancel ≥48h; $100 fee for late/no‑show." },
    { q: "What to do in case of an emergency?", a: "Call 911 or 988 or go to nearest hospital." }
  ]
};
